// Vista cross-dossier: grafo + directorio searchable de TODA la red
// política de SN que la plataforma fue acumulando.
//
// Mejoras de legibilidad:
// - Hover en un nodo resalta sus vecinos y atenúa el resto.
// - Click en un row del directorio focusea y selecciona ese nodo.
// - Filtros por tipo (persona/empresa/etc) + kind de conexión
//   (family/business/political/...) con chips arriba del grafo.
// - Toggle para ocultar nodos aislados (sin conexión) → limita ruido.
// - Modo fullscreen para mirar el grafo completo.
// - Auto-fit cada vez que cambian filtros.
// - Tamaño de nodo proporcional a importancia, grosor de edge a
//   "veces corroborado entre dossiers".

import React, { useEffect, useMemo, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';
import {
  FaSearch,
  FaUserCircle,
  FaIndustry,
  FaBuilding,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaLightbulb,
  FaTimes,
  FaExclamationTriangle,
  FaProjectDiagram,
  FaExternalLinkAlt,
  FaExpand,
  FaCompress,
  FaCrosshairs,
} from 'react-icons/fa';
import {
  borderSoft,
  panelDeep,
  mono,
  accent,
  TechInput,
  TechChip,
  Loading,
  ErrorBox,
  Empty,
} from '../../styles/techComponents';
import { getGlobalNetwork, getNetworkEntity } from '../../services/investigationService';

const TYPE_COLOR = {
  person:        { bg: '#00b4d8', border: '#0094b8' },
  company:       { bg: '#f4a261', border: '#d18443' },
  organization:  { bg: '#a78bfa', border: '#7c63d6' },
  place:         { bg: '#06d6a0', border: '#04a87f' },
  event:         { bg: '#ef476f', border: '#c93558' },
  concept:       { bg: '#fbbf24', border: '#d1981a' },
};

const KIND_COLOR = {
  family:      '#ef476f',
  business:    '#f4a261',
  political:   '#00b4d8',
  employment:  '#a78bfa',
  financial:   '#06d6a0',
  legal:       '#fbbf24',
  social:      '#9ca3af',
  historical:  '#7e7e7e',
  rivalry:     '#ef4444',
  other:       '#6b7280',
};

const TYPE_LABEL = {
  person: 'Persona',
  company: 'Empresa',
  organization: 'Organización',
  place: 'Lugar',
  event: 'Evento',
  concept: 'Concepto',
};

const KIND_LABEL = {
  family: 'Familiar',
  business: 'Comercial',
  political: 'Política',
  employment: 'Laboral',
  financial: 'Financiera',
  legal: 'Legal',
  social: 'Social',
  historical: 'Histórica',
  rivalry: 'Rivalidad',
  other: 'Otra',
};

function typeIcon(type) {
  if (type === 'person') return <FaUserCircle />;
  if (type === 'company') return <FaIndustry />;
  if (type === 'organization') return <FaBuilding />;
  if (type === 'place') return <FaMapMarkerAlt />;
  if (type === 'event') return <FaCalendarAlt />;
  return <FaLightbulb />;
}

const Layout = styled.div`
  display: grid;
  grid-template-columns: ${({ $fs }) => ($fs ? '1fr' : 'minmax(0, 1.45fr) 360px')};
  gap: 1rem;
  @media (max-width: 1100px) { grid-template-columns: 1fr; }
`;

const GraphCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChipBar = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  align-items: center;
  background: rgba(11, 19, 32, 0.4);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  padding: 0.5rem 0.6rem;

  .lbl {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-right: 5px;
  }
`;

const FilterChip = styled.button`
  background: ${({ $active, $color }) => ($active ? `${$color}24` : 'transparent')};
  border: 1px solid ${({ $active, $color }) => ($active ? `${$color}99` : borderSoft)};
  color: ${({ $active, $color }) => ($active ? $color : 'var(--text-secondary)')};
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-family: ${mono};
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  &:hover { color: ${({ $color }) => $color}; }
  .dot { width: 8px; height: 8px; border-radius: 50%; background: ${({ $color }) => $color}; }
`;

const GraphWrap = styled.div`
  position: relative;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  height: ${({ $fs }) => ($fs ? '78vh' : '620px')};
  overflow: hidden;
`;

const Legend = styled.div`
  position: absolute;
  bottom: 10px; left: 12px;
  background: rgba(11, 19, 32, 0.88);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ${mono};
  font-size: 0.58rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
  z-index: 5;
  display: flex;
  gap: 14px;

  .col {
    display: flex;
    flex-direction: column;
    gap: 3px;
    .hdr { color: var(--text-secondary); font-weight: 600; margin-bottom: 2px; }
    .row { display: flex; gap: 5px; align-items: center; }
    .dot { width: 9px; height: 9px; border-radius: 50%; }
    .bar { width: 14px; height: 2px; }
  }
`;

const Toolbar = styled.div`
  position: absolute;
  top: 10px; right: 12px;
  display: flex; gap: 6px;
  z-index: 5;
  button {
    background: rgba(11, 19, 32, 0.85);
    border: 1px solid ${borderSoft};
    color: var(--text-secondary);
    padding: 5px 10px;
    border-radius: 5px;
    font-size: 0.72rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    &:hover { color: ${accent}; border-color: rgba(0, 180, 216, 0.4); }
  }
`;

const Directory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  min-width: 0;
`;

const TypeFilters = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
`;

const TypeChipBtn = styled.button`
  background: ${({ $active, $color }) => ($active ? `${$color}22` : 'transparent')};
  border: 1px solid ${({ $active, $color }) => ($active ? `${$color}88` : borderSoft)};
  color: ${({ $active, $color }) => ($active ? $color : 'var(--text-secondary)')};
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 0.72rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { color: ${({ $color }) => $color}; }
`;

const EntityRow = styled.button`
  background: ${panelDeep};
  border: 1px solid ${({ $selected }) => ($selected ? 'rgba(0, 180, 216, 0.6)' : borderSoft)};
  border-left: 3px solid ${({ $color }) => $color};
  border-radius: 7px;
  padding: 0.55rem 0.7rem;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: 26px 1fr auto;
  align-items: center;
  gap: 0.55rem;
  transition: all 0.15s ease;
  ${({ $selected }) => $selected ? 'background: rgba(0, 180, 216, 0.06);' : ''}
  &:hover {
    border-color: rgba(0, 180, 216, 0.45);
    background: rgba(0, 180, 216, 0.04);
  }
  .icon { color: ${({ $color }) => $color}; font-size: 1.05rem; }
  .name {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 0.86rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sub {
    font-family: ${mono};
    font-size: 0.58rem;
    letter-spacing: 0.13em;
    text-transform: uppercase;
    color: var(--text-disabled);
    display: flex;
    gap: 4px;
    align-items: center;
    margin-top: 2px;
  }
  .right {
    text-align: right;
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--text-disabled);
    line-height: 1.35;
    .big {
      color: var(--text-primary);
      font-size: 0.9rem;
      font-weight: 700;
    }
  }
  .badge-warn {
    color: var(--alert-warning, #f4a261);
    background: rgba(244, 162, 97, 0.1);
    border: 1px solid rgba(244, 162, 97, 0.35);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.55rem;
  }
  .badge-shared {
    color: ${accent};
    background: rgba(0, 180, 216, 0.08);
    border: 1px solid rgba(0, 180, 216, 0.35);
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.55rem;
  }
`;

const ModalBackdrop = styled.div`
  position: fixed; inset: 0; z-index: 1500;
  background: rgba(8, 14, 24, 0.7);
  backdrop-filter: blur(3px);
  display: flex; align-items: center; justify-content: center; padding: 1rem;
`;
const ModalCard = styled.div`
  width: min(820px, 100%);
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  background: var(--color-primary);
  border: 1px solid rgba(0, 180, 216, 0.35);
  border-radius: 12px;
  padding: 1.2rem 1.3rem;
  color: var(--text-secondary);
  position: relative;
  overflow-y: auto;
  h2 { color: var(--text-primary); margin: 0 0 0.4rem; display: flex; align-items: center; gap: 8px; }
  .close {
    position: absolute; top: 12px; right: 14px;
    background: transparent; border: 1px solid ${borderSoft};
    color: var(--text-disabled);
    width: 28px; height: 28px; border-radius: 6px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    &:hover { color: var(--alert-error); }
  }
  .summary {
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-left: 2px solid ${accent};
    border-radius: 6px;
    padding: 0.6rem 0.85rem;
    margin: 0.4rem 0 0.85rem;
    font-size: 0.86rem;
    line-height: 1.55;
    color: var(--text-secondary);
  }
  .section-h {
    font-family: ${mono};
    font-size: 0.62rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin: 0.85rem 0 0.4rem;
  }
  .mention {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 8px 10px;
    background: rgba(11, 19, 32, 0.5);
    border: 1px solid ${borderSoft};
    border-radius: 6px;
    margin-bottom: 5px;
    color: var(--text-secondary);
    text-decoration: none;
    font-size: 0.84rem;
    transition: all 0.15s ease;
    &:hover {
      border-color: rgba(0, 180, 216, 0.4);
      background: rgba(0, 180, 216, 0.04);
      color: var(--text-primary);
    }
    .swatch { width: 10px; height: 10px; border-radius: 2px; flex-shrink: 0; }
    .meta {
      margin-left: auto;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--text-disabled);
      display: flex;
      gap: 4px;
    }
  }
  .conn-row {
    display: flex; align-items: center;
    gap: 8px;
    padding: 7px 10px;
    background: rgba(11, 19, 32, 0.45);
    border: 1px solid ${borderSoft};
    border-radius: 6px;
    margin-bottom: 5px;
    font-size: 0.84rem;
    .arr { color: var(--text-disabled); }
    .name { color: var(--text-primary); font-weight: 600; }
    .kind {
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.13em;
      text-transform: uppercase;
      padding: 2px 6px;
      border-radius: 4px;
      border: 1px solid;
    }
    .occ {
      margin-left: auto;
      font-family: ${mono};
      font-size: 0.6rem;
      letter-spacing: 0.13em;
      color: var(--text-disabled);
    }
  }
`;

function EntityModal({ normSlug, onClose, onSelectOther }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await getNetworkEntity(normSlug);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [normSlug]);

  if (loading) {
    return (
      <ModalBackdrop onClick={onClose}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <button className="close" onClick={onClose}><FaTimes /></button>
          <Loading>Cargando…</Loading>
        </ModalCard>
      </ModalBackdrop>
    );
  }
  if (err || !data) {
    return (
      <ModalBackdrop onClick={onClose}>
        <ModalCard onClick={(e) => e.stopPropagation()}>
          <button className="close" onClick={onClose}><FaTimes /></button>
          <ErrorBox>{err || 'No encontrado'}</ErrorBox>
        </ModalCard>
      </ModalBackdrop>
    );
  }

  const firstMention = data.mentions?.[0];
  const type = firstMention?.type || 'concept';
  const palette = TYPE_COLOR[type] || TYPE_COLOR.concept;
  const bestSummary = data.mentions
    ?.map((m) => m.summary)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)[0];
  const denunciasMentions = data.mentions?.filter((m) => {
    const p = m.profile || {};
    return Array.isArray(p.denuncias_y_causas_judiciales) && p.denuncias_y_causas_judiciales.length > 0;
  }) || [];

  return (
    <ModalBackdrop onClick={onClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <button className="close" onClick={onClose}><FaTimes /></button>
        <h2>
          <span style={{ color: palette.bg }}>{typeIcon(type)}</span>
          {firstMention?.name}
        </h2>
        <div style={{ fontFamily: mono, fontSize: '0.62rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-disabled)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span>{TYPE_LABEL[type] || type}</span>
          <span>· en {data.mentions.length} dossier{data.mentions.length === 1 ? '' : 's'}</span>
          <span>· {data.connections.length} conex.</span>
          {denunciasMentions.length > 0 && (
            <span style={{ color: 'var(--alert-warning, #f4a261)' }}>
              ⚠ Denuncias documentadas en {denunciasMentions.length} dossier(s)
            </span>
          )}
        </div>

        {bestSummary && <div className="summary">{bestSummary}</div>}

        <div className="section-h">Aparece en ({data.mentions.length})</div>
        {data.mentions.map((m) => (
          <Link
            key={m.id}
            className="mention"
            to={`/investigations/${m.investigation_slug}`}
            onClick={onClose}
          >
            <span className="swatch" style={{ background: m.cover_color || accent }} />
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              <strong>{m.investigation_title}</strong>
            </span>
            <span className="meta">
              <span>imp {m.importance}</span>
              <span>· {m.status}</span>
              <FaExternalLinkAlt style={{ fontSize: '0.66rem' }} />
            </span>
          </Link>
        ))}

        {data.connections.length > 0 && (
          <>
            <div className="section-h">Conexiones agregadas ({data.connections.length})</div>
            {data.connections.map((c, i) => {
              const otherNorm = c.from_norm === normSlug.toLowerCase() ? c.to_norm : c.from_norm;
              const otherName = c.from_norm === normSlug.toLowerCase() ? c.to_name : c.from_name;
              const kindColor = KIND_COLOR[c.kind] || '#6b7280';
              return (
                <div key={i} className="conn-row">
                  <span className="arr">↔</span>
                  <button
                    type="button"
                    className="name"
                    onClick={() => onSelectOther(otherNorm)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', textDecoration: 'underline', color: 'var(--text-primary)' }}
                  >
                    {otherName}
                  </button>
                  <span
                    className="kind"
                    style={{
                      color: kindColor,
                      borderColor: `${kindColor}66`,
                      background: `${kindColor}15`,
                    }}
                  >
                    {c.kind}
                  </span>
                  <span className="occ">
                    {c.occurrences > 1 ? `${c.occurrences}× corroborado` : '1 dossier'}
                    {c.avg_strength ? ` · fuerza ${c.avg_strength}` : ''}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </ModalCard>
    </ModalBackdrop>
  );
}

export default function GlobalNetwork() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTypes, setActiveTypes] = useState(new Set());
  const [activeKinds, setActiveKinds] = useState(new Set());
  const [hideIsolated, setHideIsolated] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState(null);
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const nodesDsRef = useRef(null);
  const edgesDsRef = useRef(null);
  const neighborsMapRef = useRef(new Map()); // slug → Set(adjacent slugs)

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const d = await getGlobalNetwork();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const entities = data?.entities || [];
  const connections = data?.connections || [];
  const stats = data?.stats || {};

  // Filtros
  const filteredConnections = useMemo(() => {
    return connections.filter((c) => {
      if (activeKinds.size > 0 && !activeKinds.has(c.kind)) return false;
      return true;
    });
  }, [connections, activeKinds]);

  // Set de slugs con al menos 1 conexión visible
  const connectedSlugs = useMemo(() => {
    const s = new Set();
    for (const c of filteredConnections) { s.add(c.from_norm); s.add(c.to_norm); }
    return s;
  }, [filteredConnections]);

  const filteredEntities = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entities.filter((e) => {
      if (activeTypes.size > 0 && !activeTypes.has(e.type)) return false;
      if (hideIsolated && !connectedSlugs.has(e.norm_slug)) return false;
      if (q && !(e.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [entities, search, activeTypes, hideIsolated, connectedSlugs]);

  // Renderizar grafo
  useEffect(() => {
    if (!containerRef.current) return;
    if (loading || filteredEntities.length === 0) {
      // Limpiar network anterior si no hay nada que mostrar
      if (networkRef.current) {
        try { networkRef.current.destroy(); networkRef.current = null; } catch {}
      }
      return;
    }

    const slugSet = new Set(filteredEntities.map((e) => e.norm_slug));

    const nodes = filteredEntities.map((e) => {
      const palette = TYPE_COLOR[e.type] || TYPE_COLOR.concept;
      const score = (e.dossier_count || 1) * 6 + Math.log2(1 + (e.connection_count || 0)) * 4 + Math.log2(1 + (e.max_importance || 50)) * 2;
      const size = 14 + Math.min(38, score);
      return {
        id: e.norm_slug,
        label: e.name,
        title: `${e.name} (${e.type})\nEn ${e.dossier_count} dossier(s) · ${e.connection_count} conex.\nImportancia ${e.max_importance}`,
        shape: e.type === 'place' ? 'triangle' : e.type === 'event' ? 'star' : 'dot',
        size,
        color: {
          background: palette.bg,
          border: palette.border,
          highlight: { background: palette.bg, border: '#ffffff' },
        },
        font: { color: '#e6e9ef', size: 13, face: 'Inter, system-ui, sans-serif', strokeWidth: 0 },
        borderWidth: e.dossier_count > 1 ? 3 : 2,
      };
    });

    const edges = filteredConnections
      .filter((c) => slugSet.has(c.from_norm) && slugSet.has(c.to_norm))
      .map((c, i) => ({
        id: `${c.from_norm}__${c.to_norm}__${c.kind}__${i}`,
        from: c.from_norm,
        to: c.to_norm,
        label: c.occurrences > 1 ? `${c.kind} ${c.occurrences}×` : c.kind,
        title: `${c.from_name} ↔ ${c.to_name}\n${c.kind}${c.labels?.length ? ' · ' + c.labels.slice(0, 2).join(' / ') : ''}\nfuerza media: ${c.avg_strength}\ncorroborado ${c.occurrences} vez/veces`,
        color: { color: KIND_COLOR[c.kind] || '#6b7280', highlight: '#ffffff' },
        width: 1 + Math.log2(1 + c.occurrences) * 1.5 + (c.avg_strength || 50) / 100 * 2,
        arrows: { to: { enabled: false } },
        font: { color: '#9ca3af', size: 10, face: 'JetBrains Mono, monospace', strokeWidth: 0 },
        smooth: { type: 'continuous', roundness: 0.22 },
      }));

    // Mapa de vecinos para highlight on hover
    const neighbors = new Map();
    for (const e of filteredEntities) neighbors.set(e.norm_slug, new Set());
    for (const c of filteredConnections) {
      if (slugSet.has(c.from_norm) && slugSet.has(c.to_norm)) {
        neighbors.get(c.from_norm)?.add(c.to_norm);
        neighbors.get(c.to_norm)?.add(c.from_norm);
      }
    }
    neighborsMapRef.current = neighbors;

    nodesDsRef.current = new DataSet(nodes);
    edgesDsRef.current = new DataSet(edges);

    const options = {
      physics: {
        enabled: true,
        barnesHut: {
          gravitationalConstant: -12000,
          springLength: 200,
          springConstant: 0.035,
          damping: 0.6,
          avoidOverlap: 0.3,
        },
        stabilization: { iterations: 280, fit: true },
      },
      interaction: {
        hover: true,
        tooltipDelay: 120,
        navigationButtons: false,
        keyboard: { enabled: false },
        hoverConnectedEdges: false, // hacemos highlight propio
      },
      nodes: { borderWidth: 2, shadow: { enabled: true, color: 'rgba(0,0,0,0.35)', size: 8 } },
      edges: { selectionWidth: 1.5 },
    };

    if (networkRef.current) {
      try { networkRef.current.destroy(); } catch {}
    }
    const network = new Network(
      containerRef.current,
      { nodes: nodesDsRef.current, edges: edgesDsRef.current },
      options
    );
    networkRef.current = network;

    // Highlight on hover: atenúa todo lo no vecino
    const highlight = (slug) => {
      const adj = neighborsMapRef.current.get(slug) || new Set();
      const updates = filteredEntities.map((e) => {
        const palette = TYPE_COLOR[e.type] || TYPE_COLOR.concept;
        const isFocused = e.norm_slug === slug || adj.has(e.norm_slug);
        return {
          id: e.norm_slug,
          opacity: isFocused ? 1 : 0.18,
          font: { color: isFocused ? '#ffffff' : 'rgba(230, 233, 239, 0.35)', size: 13 },
          color: isFocused
            ? { background: palette.bg, border: '#ffffff' }
            : { background: palette.bg, border: palette.border },
        };
      });
      nodesDsRef.current.update(updates);
      // Edges
      const edgeUpdates = edges.map((ed) => {
        const isFocused = ed.from === slug || ed.to === slug;
        return {
          id: ed.id,
          color: isFocused
            ? { color: KIND_COLOR[ed.label.split(' ')[0]] || '#fff', opacity: 1 }
            : { color: '#3a3f4b', opacity: 0.2 },
        };
      });
      edgesDsRef.current.update(edgeUpdates);
    };

    const resetHighlight = () => {
      const updates = filteredEntities.map((e) => ({
        id: e.norm_slug,
        opacity: 1,
        font: { color: '#e6e9ef', size: 13 },
        color: {
          background: (TYPE_COLOR[e.type] || TYPE_COLOR.concept).bg,
          border: (TYPE_COLOR[e.type] || TYPE_COLOR.concept).border,
        },
      }));
      nodesDsRef.current.update(updates);
      const edgeUpdates = edges.map((ed) => ({
        id: ed.id,
        color: { color: KIND_COLOR[ed.label.split(' ')[0]] || '#6b7280', opacity: 1 },
      }));
      edgesDsRef.current.update(edgeUpdates);
    };

    network.on('hoverNode', (params) => highlight(params.node));
    network.on('blurNode', resetHighlight);
    network.on('selectNode', (params) => {
      if (params.nodes?.length > 0) {
        highlight(params.nodes[0]);
        setSelectedSlug(params.nodes[0]);
      }
    });
    network.on('deselectNode', resetHighlight);
    network.on('click', (params) => {
      if (!params.nodes?.length && !params.edges?.length) resetHighlight();
    });

    return () => {
      try { network.destroy(); } catch {}
    };
  }, [filteredEntities, filteredConnections, loading]);

  if (loading) return <Loading>Cargando red global…</Loading>;
  if (err) return <ErrorBox>{err}</ErrorBox>;
  if (entities.length === 0) {
    return (
      <Empty>
        Todavía no hay entidades en ningún dossier. Creá un dossier y dejá que el autopilot
        empiece a investigar — las entidades y conexiones aparecerán acá unificadas.
      </Empty>
    );
  }

  const allTypes = Object.keys(stats.by_type || {}).sort();
  const allKinds = Object.keys(stats.by_kind || {}).sort();

  const toggleType = (t) => setActiveTypes((cur) => {
    const next = new Set(cur);
    if (next.has(t)) next.delete(t); else next.add(t);
    return next;
  });
  const toggleKind = (k) => setActiveKinds((cur) => {
    const next = new Set(cur);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const focusNode = (slug) => {
    if (!networkRef.current) return;
    networkRef.current.focus(slug, {
      scale: 1.4,
      animation: { duration: 600, easingFunction: 'easeInOutQuart' },
    });
    networkRef.current.selectNodes([slug]);
    setSelectedSlug(slug);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '0.7rem' }}>
        <TechChip>
          <FaProjectDiagram /> {stats.entities_total} entidades · {stats.connections_total} conexiones
        </TechChip>
        {stats.shared_count > 0 && (
          <TechChip>{stats.shared_count} compartidas entre dossiers</TechChip>
        )}
        {stats.with_denuncias > 0 && (
          <TechChip tone="warn">
            <FaExclamationTriangle /> {stats.with_denuncias} con denuncias
          </TechChip>
        )}
        <TechChip>
          Mostrando {filteredEntities.length} · {filteredConnections.filter((c) => {
            const s = new Set(filteredEntities.map((e) => e.norm_slug));
            return s.has(c.from_norm) && s.has(c.to_norm);
          }).length} edges
        </TechChip>
      </div>

      <Layout $fs={fullscreen}>
        <GraphCol>
          <ChipBar>
            <span className="lbl">Tipo</span>
            {allTypes.map((t) => {
              const palette = TYPE_COLOR[t] || TYPE_COLOR.concept;
              const count = stats.by_type[t];
              return (
                <FilterChip
                  key={t}
                  $active={activeTypes.has(t)}
                  $color={palette.bg}
                  onClick={() => toggleType(t)}
                >
                  <span className="dot" />
                  {TYPE_LABEL[t] || t} · {count}
                </FilterChip>
              );
            })}
          </ChipBar>

          <ChipBar>
            <span className="lbl">Vínculo</span>
            {allKinds.map((k) => {
              const color = KIND_COLOR[k] || '#6b7280';
              const count = stats.by_kind[k];
              return (
                <FilterChip
                  key={k}
                  $active={activeKinds.has(k)}
                  $color={color}
                  onClick={() => toggleKind(k)}
                >
                  <span className="dot" />
                  {KIND_LABEL[k] || k} · {count}
                </FilterChip>
              );
            })}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 5 }}>
              <FilterChip
                $active={hideIsolated}
                $color="#9ca3af"
                onClick={() => setHideIsolated((v) => !v)}
                title="Ocultar nodos sin conexión"
              >
                {hideIsolated ? '☐ aislados ocultos' : '☑ mostrar aislados'}
              </FilterChip>
            </span>
          </ChipBar>

          <GraphWrap $fs={fullscreen}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
            <Legend>
              <div className="col">
                <div className="hdr">Tipo</div>
                {Object.entries(TYPE_LABEL).map(([t, lbl]) => (
                  <div key={t} className="row">
                    <span className="dot" style={{ background: TYPE_COLOR[t].bg }} /> {lbl}
                  </div>
                ))}
              </div>
              <div className="col">
                <div className="hdr">Tamaño</div>
                <div className="row">+ dossiers</div>
                <div className="row">+ conexiones</div>
                <div className="row">+ importancia</div>
              </div>
              <div className="col">
                <div className="hdr">Edge</div>
                <div className="row"><span className="bar" style={{ background: '#9ca3af' }} /> 1 dossier</div>
                <div className="row"><span className="bar" style={{ height: 4, background: '#fff' }} /> n× corrob.</div>
              </div>
            </Legend>
            <Toolbar>
              <button type="button" onClick={() => networkRef.current?.fit({ animation: { duration: 600 } })}>
                <FaCrosshairs /> Encuadrar
              </button>
              <button type="button" onClick={() => {
                const opts = networkRef.current?.physics?.options;
                if (opts) networkRef.current.setOptions({ physics: { enabled: !opts.enabled } });
              }}>
                ⏯ Física
              </button>
              <button type="button" onClick={() => setFullscreen((v) => !v)}>
                {fullscreen ? <FaCompress /> : <FaExpand />}
                {fullscreen ? 'Salir' : 'Expandir'}
              </button>
            </Toolbar>
          </GraphWrap>
        </GraphCol>

        {!fullscreen && (
          <Directory>
            <div style={{ position: 'relative' }}>
              <FaSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-disabled)', fontSize: '0.82rem' }} />
              <TechInput
                placeholder="Buscar en la red…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: 32 }}
              />
            </div>

            <TypeFilters>
              {allTypes.map((t) => {
                const palette = TYPE_COLOR[t] || TYPE_COLOR.concept;
                const count = stats.by_type[t];
                return (
                  <TypeChipBtn
                    key={t}
                    $active={activeTypes.has(t)}
                    $color={palette.bg}
                    onClick={() => toggleType(t)}
                  >
                    {TYPE_LABEL[t] || t} · {count}
                  </TypeChipBtn>
                );
              })}
            </TypeFilters>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: 5,
              maxHeight: 560, overflowY: 'auto', paddingRight: 4,
            }}>
              {filteredEntities.length === 0 ? (
                <div style={{ color: 'var(--text-disabled)', fontSize: '0.82rem', padding: '0.8rem 0.2rem' }}>
                  Sin entidades con esos filtros.
                </div>
              ) : filteredEntities.map((e) => {
                const palette = TYPE_COLOR[e.type] || TYPE_COLOR.concept;
                const hasDenuncias = Array.isArray(e.mentions) && e.mentions.some((m) => m.has_denuncias);
                const isSelected = selectedSlug === e.norm_slug;
                return (
                  <EntityRow
                    key={e.norm_slug}
                    $color={palette.bg}
                    $selected={isSelected}
                    onClick={() => focusNode(e.norm_slug)}
                    onDoubleClick={() => setSelectedSlug(e.norm_slug)}
                    title="Click: focusear en el grafo · Doble click: abrir detalle"
                  >
                    <span className="icon">{typeIcon(e.type)}</span>
                    <div style={{ minWidth: 0 }}>
                      <div className="name">{e.name}</div>
                      <div className="sub">
                        {TYPE_LABEL[e.type] || e.type}
                        {e.dossier_count > 1 && <span className="badge-shared">{e.dossier_count} dossiers</span>}
                        {hasDenuncias && <span className="badge-warn">⚠ denuncias</span>}
                      </div>
                    </div>
                    <div className="right">
                      <div className="big">{e.connection_count}</div>
                      <div>conex.</div>
                    </div>
                  </EntityRow>
                );
              })}
            </div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-disabled)', fontFamily: mono, letterSpacing: '0.14em' }}>
              Click → focus · Doble click → detalle
            </div>
          </Directory>
        )}
      </Layout>

      {selectedSlug && (
        <EntityModal
          normSlug={selectedSlug}
          onClose={() => setSelectedSlug(null)}
          onSelectOther={(slug) => { setSelectedSlug(slug); focusNode(slug); }}
        />
      )}
    </>
  );
}
